import * as React from "react";
import {
  Button,
  Table,
  Pagination,
  Tag,
  Empty,
  Form,
  Input,
  Row,
  Col,
  Image,
  Select,
  Upload,
  notification,
  Checkbox,
  Tooltip,
  Spin,
  Switch,
} from "antd";
import {
  DownOutlined,
  UpOutlined,
  WarningOutlined,
  EyeInvisibleOutlined,
  IssuesCloseOutlined,
} from "@ant-design/icons";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  CinemContainer,
  NewLink,
  LeftContainer,
  RightContainer,
  Categories,
  CategoryData,
  Category,
  Color,
  CategoryNameContainer,
  ButtonContainer,
  PaginatorContainer,
  MovieTitle,
  MovieEditButtons,
  ColoredText,
} from "./style";
import {
  getLastShowDeploy,
  getSelectedLocation,
} from "../../logic/redux/app/app-selectors";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import {
  cinema_labels,
  cinema_labelsVariables,
  cinema_labels_cinema_labels_labels,
} from "./__generated__/cinema_labels";
import {
  moviesQuery,
  moviesQueryVariables,
  moviesQuery_movies_movies,
  moviesQuery_movies_movies_labels,
} from "./__generated__/moviesQuery";

import { save_movieVariables, save_movie } from "./__generated__/save_movie";

import { queryUpdater } from "../../helpers/utils";
import { API_URL } from "../../generated/graphql-apollo";

import MovieShowtimes from "../../components/movie_showtimes/movie_showtimes";
import App from "../../logic/redux/app";
import { DateTime } from "luxon";
import { RenderExpandIconProps } from "rc-table/lib/interface";
import { CustomTagProps } from "rc-select/lib/interface/generator";

const { Option } = Select;
const { TextArea } = Input;

export const PAGE = "page";

function CinemaContent(): JSX.Element {
  const [form] = Form.useForm();
  const history = useHistory();
  const search = useLocation().search;
  const dispatch = useDispatch();
  const page = new URLSearchParams(search).get("page");
  const selectedLocation = useSelector(getSelectedLocation);
  const lastDeploy = useSelector(getLastShowDeploy);
  const per_page = 10;

  const [actualPage, setActualPage] = React.useState<number>(1);
  const [selectedCategory, setSelectedCategory] = React.useState<number>(-1);
  const [openedCategory, setOpenedCategory] =
    React.useState<number | undefined>(undefined);
  const [movieExpandedKeys, setMovieExpandedKeys] = React.useState<number[]>(
    []
  );

  const [withShowtimesSelect, setWithShowtimesSelect] =
    React.useState<boolean>(true);

  const [movieShowtimesExpandedKeys, setMovieShowtimesExpandedKeys] =
    React.useState<number[]>([]);

  const [
    getLabels,
    { data: labels, loading: label_loading, error: label_error },
  ] = useLazyQuery<cinema_labels, cinema_labelsVariables>(
    gql`
      query cinema_labels($location_id: Int!) {
        cinema_labels(location_id: $location_id) {
          movie_count
          movie_count_without_label
          labels {
            id
            name
            color
            text_color
            translations
            deletable
            movie_count
          }
        }
      }
    `,
    {
      variables: { location_id: selectedLocation },
    }
  );

  React.useEffect(() => {
    if (label_error) {
      notification["error"]({
        message: "Hiba történt a kategóriák betöltésében",
      });
    }
  }, [label_error]);

  const [
    getMovies,
    { data: movies, loading: movie_loading, error: movie_error },
  ] = useLazyQuery<moviesQuery, moviesQueryVariables>(
    gql`
      query moviesQuery($input: MovieListRequest!, $location_id: Int!) {
        movies(input: $input, location_id: $location_id) {
          page_count
          movies {
            id
            external_id
            image {
              image
              base64
            }
            rating
            length
            country
            director
            description
            visible
            available
            labels {
              id
              name
              color
              text_color
            }
            title_original
            title_translated
            visual
            origin
            speech
            original_language
            subtitle_language
            scriptwriter
            photographer
            actors
            updated_at
          }
        }
      }
    `,
    {
      variables: {
        input: {
          page: actualPage,
          per_page: per_page,
          label_id: selectedCategory,
          with_showtime: withShowtimesSelect,
        },
        location_id: selectedLocation,
      },
      fetchPolicy: "network-only",
    }
  );

  React.useEffect(() => {
    if (selectedLocation > 0) {
      getMovies({
        variables: {
          location_id: selectedLocation,
          input: {
            page: actualPage,
            per_page: per_page,
            label_id: selectedCategory,
            with_showtime: withShowtimesSelect,
          },
        },
      });
    }
  }, [
    selectedCategory,
    selectedLocation,
    actualPage,
    per_page,
    withShowtimesSelect,
  ]);

  React.useEffect(() => {
    if (movie_error) {
      notification["error"]({
        message: "Hiba történt a filmek betöltésében",
      });
    }
  }, [movie_error]);

  const [
    updateMovie,
    { loading: updateMovieLoading, error: updateMovieError },
  ] = useMutation<save_movie, save_movieVariables>(gql`
    mutation save_movie($movie: InputMovie!, $location_id: Int!) {
      save_movie(movie: $movie, location_id: $location_id) {
        id
      }
    }
  `);

  React.useEffect(() => {
    if (updateMovieError) {
      notification["error"]({
        message: "Hiba történt az adatok mentésekor",
      });
    }
  }, [updateMovieError]);

  function onPagerChange(value: number) {
    setMovieExpandedKeys([]);
    const querystring = queryUpdater(search, PAGE, value.toString());
    history.push(`${history.location.pathname}${querystring}`);
    //
  }

  React.useEffect(() => {
    if (page) {
      let pageNumber = Number(page);
      if (pageNumber) {
        if (pageNumber < 1) {
          pageNumber = 1;
          const querystring = queryUpdater(search, PAGE, pageNumber.toString());
          history.push(`${history.location.pathname}${querystring}`);
        } else {
          setActualPage(pageNumber);
        }
      } else {
        pageNumber = 1;
        const querystring = queryUpdater(search, PAGE, pageNumber.toString());
        history.push(`${history.location.pathname}${querystring}`);
      }
    }
    if (page && movies) {
      let pageNumber = Number(page);
      if (pageNumber > movies.movies.page_count) {
        pageNumber = 1;
        const querystring = queryUpdater(search, PAGE, pageNumber.toString());
        history.push(`${history.location.pathname}${querystring}`);
      }
    }
  }, [page, movies]);

  React.useEffect(() => {
    if (movies) {
      dispatch(App.actions.setLastMovieQuery(DateTime.now()));
    }
  }, [movies]);

  React.useEffect(() => {
    if (selectedLocation > 0) {
      getLabels({ variables: { location_id: selectedLocation } });
    }
  }, [selectedLocation]);

  React.useEffect(() => {
    if (labels) {
      const labelss: cinema_labels_cinema_labels_labels[] = [];
      labelss.push({
        __typename: "LabelWithMovieCount",
        id: -1,
        name: "Összes film",
        color: "",
        translations: undefined,
        deletable: false,
        movie_count: labels.cinema_labels.movie_count,
        text_color: "",
      });

      labels.cinema_labels.labels.map(
        (label: cinema_labels_cinema_labels_labels) => {
          //labelss.push(renderCategory(label, true, label.deletable));
          labelss.push(label);
          //renderCategoryEdit(label);
        }
      );

      labelss.push({
        __typename: "LabelWithMovieCount",
        id: -2,
        name: "Kategória nélküliek",
        color: "",
        translations: undefined,
        deletable: false,
        movie_count: labels.cinema_labels.movie_count_without_label,
        text_color: "",
      });
      //setCinemaLabels(labelss);

      /*
      categories.push(
        renderCategory(
          {
            id: -1,
            name: "Összes film",
            color: "",
            translations: undefined,
            deletable: false,
            movie_count: labels.cinema_labels.movie_count,
          },
          false,
          false
        )
      );
      
      categories.push(
        renderCategory(
          {
            id: -2,
            name: "Kategória nélküliek",
            color: "",
            translations: undefined,
            deletable: false,
            movie_count: labels.cinema_labels.movie_count_without_label,
          },
          false,
          false
        )
      );
      */
    }
  }, [labels]);

  const renderCategoryEdit = (
    // szerkesztésnél kelleni fog
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    label: cinema_labels_cinema_labels_labels
  ): JSX.Element => {
    function handleCancelEditCategory() {
      setOpenedCategory(undefined);
    }

    return (
      <>
        <Button onClick={handleCancelEditCategory}>Mégse</Button>
      </>
    );
  };

  const renderCategory = (
    label: cinema_labels_cinema_labels_labels,
    editable: boolean,
    // kelleni fog, ha visszakerül a törlés
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    deletable: boolean
  ): JSX.Element => {
    // kelleni fog szerkesztésnél
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleEditCategory = (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setOpenedCategory(label.id);
      setSelectedCategory(label.id);
    };

    function handleSelectCategory() {
      if (label.id !== openedCategory) {
        setMovieExpandedKeys([]);
        setOpenedCategory(undefined);
      }
      setSelectedCategory(label.id);
    }

    return (
      <Category key={"category_id_" + label.id}>
        <CategoryData
          key={"label_list_" + label.id}
          selected={selectedCategory === label.id}
          onClick={handleSelectCategory}
        >
          <Color color={label.color} textColor={label.text_color} />
          <CategoryNameContainer selected={selectedCategory === label.id}>
            {label.name + " (" + label.movie_count + ")"}
          </CategoryNameContainer>
          <ButtonContainer>
            {
              null /*editable && openedCategory !== label.id && (
              <Button
                icon={<EditOutlined />}
                onClick={handleEditCategory}
              ></Button>
            )*/
            }
          </ButtonContainer>
          <ButtonContainer>
            {null /*deletable && <Button icon={<DeleteOutlined />}></Button>*/}
          </ButtonContainer>
        </CategoryData>
        {
          editable && openedCategory === label.id && null /*(
           <CategoryEdit>{label.name}</CategoryEdit>
        )*/
        }
      </Category>
    );
  };

  const renderCategories = (): JSX.Element[] => {
    const categories: JSX.Element[] = [];
    if (labels) {
      categories.push(
        renderCategory(
          {
            __typename: "LabelWithMovieCount",
            id: -1,
            name: "Összes film",
            color: "",
            text_color: "",
            translations: undefined,
            deletable: false,
            movie_count: labels.cinema_labels.movie_count,
          },
          false,
          false
        )
      );
      labels.cinema_labels.labels.map((label) => {
        categories.push(renderCategory(label, true, label.deletable));
        renderCategoryEdit(label);
      });
      categories.push(
        renderCategory(
          {
            __typename: "LabelWithMovieCount",
            id: -2,
            name: "Kategória nélküliek",
            color: "",
            text_color: "",
            translations: undefined,
            deletable: false,
            movie_count: labels.cinema_labels.movie_count_without_label,
          },
          false,
          false
        )
      );
    }

    return categories;
  };

  const renderMovieTitle = (title_translated: string): JSX.Element => {
    return <MovieTitle>{title_translated}</MovieTitle>;
  };

  const renderMovieLabel = (
    label: moviesQuery_movies_movies_labels
  ): JSX.Element => {
    return (
      <Tag
        key={"movie_label_" + label.id}
        color={label.color}
        style={{ border: "1px solid " + label.text_color, borderRadius: "2px" }}
      >
        <ColoredText color={label.text_color}>{label.name}</ColoredText>
      </Tag>
    );
  };

  const renderMovieLabels = (
    labels: moviesQuery_movies_movies_labels[]
  ): JSX.Element => {
    return <div>{labels.map((label) => renderMovieLabel(label))}</div>;
  };

  const renderMovieWarning = (
    record: moviesQuery_movies_movies
  ): JSX.Element => {
    return record.image.image.trim() === "" ||
      record.visual.trim() === "" ||
      record.rating.trim() === "" ||
      record.title_translated.trim() === "" ? (
      <Tooltip title="nem jelenik meg a kioskon hiányzó szükséges adat miatt">
        <WarningOutlined style={{ fontSize: "22px", color: "#faad14" }} />
      </Tooltip>
    ) : (
      <></>
    );
  };

  const renderMovieNotDeployed = (
    record: moviesQuery_movies_movies
  ): JSX.Element => {
    return lastDeploy &&
      DateTime.fromISO(record.updated_at, { setZone: true }) > lastDeploy ? (
      <Tooltip title="olyan módosítást jelöl amely még nincs aktiválva a kioskra">
        <IssuesCloseOutlined style={{ fontSize: "22px", color: "#ff4d4f" }} />
      </Tooltip>
    ) : (
      <></>
    );
  };

  const renderMovieVisibleIcon = (
    record: moviesQuery_movies_movies
  ): JSX.Element => {
    return !record.visible ? (
      <Tooltip title="nem jelenik meg a kioskon">
        <EyeInvisibleOutlined
          style={{ fontSize: "22px", color: "rgba(0, 0, 0, 0.45)" }}
        />
      </Tooltip>
    ) : (
      <></>
    );
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title_translated",
      key: "title_translated",
      render: (text: string) => renderMovieTitle(text),
    },
    {
      title: "Labels",
      dataIndex: "labels",
      key: "labels",
      render: (labels: moviesQuery_movies_movies_labels[]) =>
        renderMovieLabels(labels),
    },
    {
      title: "Visual",
      dataIndex: "visual",
      key: "visual",
    },
    {
      title: "Speech",
      dataIndex: "speech",
      key: "speech",
    },
    {
      title: "Subtitle",
      dataIndex: "subtitle",
      key: "subtitle",
    },
    {
      title: "Visible",
      /*key: "warning",*/
      render: (record: moviesQuery_movies_movies) =>
        renderMovieVisibleIcon(record),
    },
    {
      title: "Warning",
      /*key: "warning",*/
      render: (record: moviesQuery_movies_movies) => renderMovieWarning(record),
    },
    {
      title: "NotDeployed",
      render: (record: moviesQuery_movies_movies) =>
        renderMovieNotDeployed(record),
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function customExpandIcon(props: RenderExpandIconProps<any>) {
    // eslint-disable-next-line react/prop-types
    if (props.expanded) {
      return (
        <a
          style={{ color: "black" }}
          onClick={(e) => {
            // eslint-disable-next-line react/prop-types
            props.onExpand(props.record, e);
          }}
        >
          <UpOutlined />
        </a>
      );
    } else {
      return (
        <a
          style={{ color: "black" }}
          onClick={(e) => {
            // eslint-disable-next-line react/prop-types
            props.onExpand(props.record, e);
          }}
        >
          <DownOutlined />
        </a>
      );
    }
  }

  function onExpandMovie(expanded: boolean, record: moviesQuery_movies_movies) {
    if (expanded) {
      form.setFieldsValue({
        ...record,
        labels: record.labels.map(({ id }) => id),
        image: {
          image: record.image.image,
          base64: "",
        },
      });
      setMovieExpandedKeys([record.id]);
    } else {
      setMovieExpandedKeys([]);
    }
    setMovieShowtimesExpandedKeys([]);
  }

  /*const renderCategoryColor = (color: string): JSX.Element => {
    return <Color color={color} />;
  };

  const categoryColumns = [
    {
      title: "Color",
      dataIndex: "color",
      key: "color",
      padding: "0px",
      render: (text: string) => renderCategoryColor(text),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "195px",
    },
    {
      title: "Visual",
      dataIndex: "visual",
      key: "visual",
    },
    {
      title: "Speech",
      dataIndex: "speech",
      key: "speech",
    },
    {
      title: "Subtitle",
      dataIndex: "subtitle",
      key: "subtitle",
    },
  ];*/

  function onMovieEditCancel() {
    setMovieExpandedKeys([]);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function onMovieEditSave(values: any) {
    const actualMovie = movies?.movies.movies.find((x) => x.id === values.id);
    const imageToSave = form.getFieldValue("image");

    const movieToSave = {
      ...actualMovie,
      ...values,
      image: imageToSave,
    };
    delete movieToSave["__typename"];

    updateMovie({
      variables: {
        movie: movieToSave,
        location_id: selectedLocation,
      },
    })
      .then(() => {
        //setMovieExpandedKeys([]);
        notification["success"]({
          message: "Az adatok sikeresen elmentve",
        });
        getMovies({
          variables: {
            location_id: selectedLocation,
            input: {
              page: actualPage,
              per_page: per_page,
              label_id: selectedCategory,
              with_showtime: withShowtimesSelect,
            },
          },
        });
        getLabels({ variables: { location_id: selectedLocation } });
      })
      .catch(() => {
        notification["error"]({
          message: "Hiba történt a mentés során",
        });
      });
    return;
  }

  function tagRender(props: CustomTagProps): React.ReactElement {
    const { label, value, closable, onClose } = props;
    const onPreventMouseDown = (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
    };
    const l = labels?.cinema_labels.labels.find((x) => x.id === value);
    return (
      <Tag
        color={l ? l.color : ""}
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        style={{
          marginRight: 3,
        }}
      >
        <ColoredText color={l ? l.text_color : ""}>{label}</ColoredText>
      </Tag>
    );
  }

  /*function handleMovieCategoryChange(data: number[]) {
    const editlabels: moviesQuery_movies_movies_labels[] = [];
    if (editMovie) {
      data.map((id) => {
        labels &&
          labels.cinema_labels.labels.map((l) => {
            if (id === l.id) {
              editlabels.push({
                id: l.id,
                color: l.color,
                name: l.name,
                __typename: "MovieLabel",
              });
            }
          });
      });
      const copyMovie = { ...editMovie };
      copyMovie.labels = editlabels;
      //setEditMovie(copyMovie);
    }
  }*/

  function getBase64(img: File, callback: (imageUrl: string) => void) {
    const reader = new FileReader();
    reader.addEventListener("load", () => callback(reader.result as string));
    reader.readAsDataURL(img);
  }

  function beforeUpload(file: File) {
    getBase64(file, (imageUrl: string | ArrayBuffer | null) => {
      form.setFieldsValue({
        image: {
          image: "",
          base64: imageUrl,
        },
      });
    });

    return false;
  }

  function renderImage(file: { image: string; base64: string }): JSX.Element {
    const token = sessionStorage.getItem("token");
    return (
      <Image
        width={250}
        height={352}
        src={
          file.base64
            ? file.base64
            : API_URL +
              "/api/image/" +
              selectedLocation +
              "/shows/" +
              file.image +
              "?token=" +
              token
        }
        fallback="data:image/png;base64,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"
        preview={false}
      />
    );
  }

  function onExpandShowtimes(
    expanded: boolean,
    record: { name: string; show_id: number }
  ) {
    if (expanded) {
      setMovieShowtimesExpandedKeys([record.show_id]);
    } else {
      setMovieShowtimesExpandedKeys([]);
    }
  }

  function renderMovieShowtime(record: { show_id: number }): JSX.Element {
    return (
      <MovieShowtimes
        show_id={record.show_id}
        expanded={movieShowtimesExpandedKeys.length === 1}
      />
    );
  }

  function renderEditMovie(record: moviesQuery_movies_movies) {
    //const id: number = form.getFieldValue("id");

    return (
      <>
        <Form
          form={form}
          layout="vertical"
          style={{ padding: "20px" }}
          onFinish={onMovieEditSave}
          initialValues={{
            ...record,
            labels: record.labels.map(({ id }) => id),
            image: {
              image: record.image.image,
              base64: "",
            },
          }}
        >
          <Row gutter={50}>
            <Col span={8}>
              <Form.Item name="id" label="ID" style={{ display: "none" }}>
                <Input disabled={true} />
              </Form.Item>

              <Form.Item name="title_translated" label="Magyar cím">
                <Input disabled={true} />
              </Form.Item>

              <Form.Item name="title_original" label="Eredeti cím">
                <Input disabled={true} />
              </Form.Item>

              <Form.Item name="length" label="Hossz (perc)">
                <Input disabled={true} />
              </Form.Item>

              <Form.Item name="country" label="Ország">
                <Input />
              </Form.Item>

              <Form.Item name="director" label="Rendező">
                <Input />
              </Form.Item>

              <Form.Item name="scriptwriter" label="Forgatókönyv író">
                <Input />
              </Form.Item>

              <Form.Item name="photographer" label="Fényképezte">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="visual" label="Vetítés típusa">
                <Input disabled={true} />
              </Form.Item>

              <Form.Item name="speech" label="Hang">
                <Input disabled={true} />
              </Form.Item>

              <Form.Item name="original_language" label="Eredeti nyelv">
                <Input />
              </Form.Item>

              <Form.Item name="subtitle_language" label="Felirat nyelve">
                <Input />
              </Form.Item>

              <Form.Item name="origin" label="Származási ország">
                <Input disabled={true} />
              </Form.Item>

              <Form.Item name="rating" label="Besorolás">
                {/*<Input />*/}
                <Select>
                  <Option value=""> </Option>
                  <Option value="6">6</Option>
                  <Option value="12">12</Option>
                  <Option value="16">16</Option>
                  <Option value="18">18</Option>
                </Select>
              </Form.Item>

              <Form.Item name="visible" label="Látható" valuePropName="checked">
                <Checkbox />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Kép (képre kattintva lehet módosítani)"
                valuePropName="file"
                shouldUpdate={true}
              >
                {({ getFieldValue }) => (
                  <Upload
                    listType="picture"
                    className="avatar-uploader"
                    showUploadList={false}
                    beforeUpload={beforeUpload}
                  >
                    {renderImage(getFieldValue("image"))}
                  </Upload>
                )}
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="labels" label="Kategóriák">
            <Select
              mode="multiple"
              /*onChange={handleMovieCategoryChange}*/
              tagRender={tagRender}
            >
              {/* TODO-JANI: Erről beszéljünk */}
              {labels &&
                labels.cinema_labels.labels.map((label) => {
                  return (
                    <Option
                      key={"movie_label_option_" + label.id}
                      value={label.id}
                    >
                      {
                        <Tag color={label.color}>
                          <ColoredText color={label ? label.text_color : ""}>
                            {label.name}
                          </ColoredText>
                        </Tag>
                      }
                    </Option>
                  );
                })}
            </Select>
          </Form.Item>

          <Form.Item name="actors" label="Szereplők">
            <Input />
          </Form.Item>

          <Form.Item name="description" label="Magyar leírás">
            <TextArea maxLength={550} showCount={true} rows={3} />
          </Form.Item>

          <Form.Item>
            <MovieEditButtons>
              <Button
                type="primary"
                htmlType="submit"
                loading={updateMovieLoading}
              >
                Mentés
              </Button>
              <Button htmlType="button" onClick={onMovieEditCancel}>
                Mégse
              </Button>
            </MovieEditButtons>
          </Form.Item>
        </Form>
        <Table
          dataSource={[{ show_id: record.id, name: "Vetítések" }]}
          columns={[
            {
              title: "name",
              dataIndex: "name",
              key: "name",
              render: (text: string) => renderMovieTitle(text),
            },
          ]}
          showHeader={false}
          style={{ width: "100%" }}
          pagination={false}
          rowKey={"show_id"}
          locale={{
            emptyText: <Empty description={"Nincs megjeleníthető adat"} />,
          }}
          expandable={{
            // eslint-disable-next-line react/display-name
            //onExpandedRowsChange: movieExpandChange,
            expandedRowRender: renderMovieShowtime,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            expandIcon: (props: RenderExpandIconProps<any>) =>
              customExpandIcon(props),
            expandRowByClick: true,
            expandedRowKeys: movieShowtimesExpandedKeys,
            onExpand: onExpandShowtimes,
          }}
        />
      </>
    );
  }

  function onChangeWithShowtimesSelect() {
    setWithShowtimesSelect(!withShowtimesSelect);
  }

  return (
    <CinemContainer>
      <LeftContainer>
        <NewLink>{/*+ Új kategória hozzáadása*/}</NewLink>
        <Categories>
          {label_loading ? <Spin /> : renderCategories()}
          {/*<CategoryTable>
            <Table
              dataSource={cinemaLabels ? cinemaLabels : []}
              columns={categoryColumns}
              showHeader={false}
              style={{ width: "100%" }}
              pagination={false}
              rowKey={"id"}
              locale={{
                emptyText: <Empty description={"Nincs megjeleníthető adat"} />,
              }}
              
            />
          </CategoryTable>*/}
        </Categories>
      </LeftContainer>
      <RightContainer>
        <Switch
          checkedChildren="Filmek aktív vetítéssel"
          unCheckedChildren="Filmek aktív vetítés nélkül"
          style={{ width: "200px" }}
          checked={withShowtimesSelect}
          onChange={onChangeWithShowtimesSelect}
        />
        <Table
          dataSource={movies ? movies.movies.movies : []}
          columns={columns}
          showHeader={false}
          style={{ width: "100%" }}
          pagination={false}
          rowKey={"id"}
          locale={{
            emptyText: <Empty description={"Nincs megjeleníthető adat"} />,
          }}
          expandable={{
            // eslint-disable-next-line react/display-name
            //onExpandedRowsChange: movieExpandChange,
            expandedRowRender: renderEditMovie,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            expandIcon: (props: RenderExpandIconProps<any>) =>
              customExpandIcon(props),
            expandRowByClick: true,
            expandedRowKeys: movieExpandedKeys,
            onExpand: onExpandMovie,
          }}
          loading={movie_loading}
        />
        <PaginatorContainer>
          <Pagination
            defaultCurrent={1}
            current={actualPage}
            pageSize={per_page}
            total={movies ? movies.movies.page_count * per_page : 1}
            onChange={onPagerChange}
            showSizeChanger={false}
          />
        </PaginatorContainer>
      </RightContainer>
    </CinemContainer>
  );
}

export default CinemaContent;
